import React, { useEffect } from 'react'

import { useDispatch, useSelector } from 'react-redux'

import orgamax from 'ampersand-app'
import lang from '@/lang'

import FileExplorerDialog from '@/modules/file/components/file-explorer-dialog'

import Panel from '@/components/layouts/panel'
import PanelHeader from '@/components/layouts/panel/panel-header'
import PanelContent from '@/components/layouts/panel/panel-content'
import PanelFileContent from '@/modules/file/components/panels/panel-file-content'
import PanelFooter from '@/components/layouts/panel/panel-footer'
import DocumentContainerClosedIcon from '@assets/images/icons/folder-closed.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'

import { assignFile, hideFileContainer } from 'redux/ducks/receipt/edit'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { FILE_LIST_FILTER } from '@/modules/base/util/constants'
import ReceiptPreviewerItem from '@/modules/receipt/preview/item/receipt-previewer-item'
import RecieptPreviewFileListPanelHeader from '@/modules/receipt/preview/file-list/panel/receipt-preview-file-list-panel-header'
import ReceiptPreviewFileListPanelFooter from '@/modules/receipt/preview/file-list/panel/receipt-preview-file-list-panel-footer'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'

/**
 * Callback to render a dialog to preview a selected receipt file
 *
 * @param file
 * @param handleClosePreview
 * @param handleRemove
 * @param handleAbort
 * @param handleSelect
 * @returns {*}
 */
const renderPreviewPanel = (file, { handleClosePreview, handleRemove, handleAbort, handleSelect, dateFor }) => {
  return (
    <Panel position="absolute">
      <PanelHeader
        onClose={handleClosePreview}
        displayClose={false}>
        <RecieptPreviewFileListPanelHeader dateValue={file?.recordDate} nameValue={file?.name} />
      </PanelHeader>
      <PanelContent>
        <PanelFileContent>
          <ReceiptPreviewerItem
            disableActions
            key={file.id}
            item={file}
            dateFor={dateFor}
          />
        </PanelFileContent>
      </PanelContent>
      <PanelFooter>
        <ReceiptPreviewFileListPanelFooter
          removeLabel={lang.expense.receipt.remove}
          abortLabel={lang.generics.abort}
          selectLabel={lang.generics.select}
          downloadLabel={lang.generics.download}
          downloadUrl={`${file.url}?token=${orgamax.user.token}`}
          onRemove={handleRemove}
          onAbort={handleAbort}
          onSelect={handleSelect} />
      </PanelFooter>
    </Panel>
  )
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  close: {
    display: 'flex',
    alignItems: 'center',
    color: theme.primaryDark,
    fontWeight: theme.fontWeights.medium,
    cursor: 'pointer',
    padding: '30px 20px 0px 0px',
    '&:hover': {
      color: theme.primaryBright,
      '& > $closeIcon': {
        fill: theme.primaryBright,
      },
    },
  },
  closeIcon: {
    marginRight: 5,
    fill: theme.primaryDark,
  },
}))

const ReceiptPreviewFileList = props => {
  const dispatch = useDispatch()
  const classes = useStyles()

  const { files: assignedFiles } = props
  const assignedFileIds = assignedFiles.map(file => file.id)

  const { error } = useSelector(state => state.document.list)

  useEffect(() => {
    if (error) {
      const message = lang.defaultErrorMessage
      toast.showToast({ type: 'error', message })
    }
  }, [error])

  const onClose = () => {
    dispatch(hideFileContainer())
  }

  const onSelectFile = file => {
    dispatch(assignFile(file))
    dispatch(hideFileContainer())
    // FLAG:useDocuAi — absent for legacy and saved receipts; their assignment path is unchanged.
    props.onSelectDocument?.(file)
  }

  const customClose = (
    <div className={classes.close} onClick={onClose}>
      <SVGIcon className={classes.closeIcon} svg={DocumentContainerClosedIcon} height='13px' width='13px' />
      <div>{lang.receipt.documentContainer.close}</div>
    </div>
  )

  return (
    <FileExplorerDialog
      customClose={customClose}
      onClose={onClose}
      onSelectFile={onSelectFile}
      previewDialog={renderPreviewPanel}
      filter={FILE_LIST_FILTER.NO_SYSTEM_DOCUMENTS}
      excludes= {assignedFileIds}
      excludeOfficeDocuments
    />
  )
}

export default ReceiptPreviewFileList
